import Layout from '@redirector/front/components/layout/Layout';
import { useAuth } from '@redirector/front/context/useAuth';
import { lazy, type ReactNode, Suspense } from 'react';
import { Navigate,Route, Routes } from 'react-router-dom';

/**
 * Topological Code Splitting
 * These components are removed from the main entry bundle and
 * loaded on-demand via separate network requests.
 */
const RedirectionsPage = lazy(() => import('@redirector/front/pages/RedirectionsPage'));
const UsersPage = lazy(() => import('@redirector/front/pages/UsersPage'));
const GroupsPage = lazy(() => import('@redirector/front/pages/GroupsPage'));
const RolesPage = lazy(() => import('@redirector/front/pages/RolesPage'));
const StatsPage = lazy(() => import('@redirector/front/pages/StatsPage'));
const StatsDetailPage = lazy(() => import('@redirector/front/pages/StatsDetailPage'));
const Login = lazy(() => import('@redirector/front/pages/Login'));
const ResetPassword = lazy(() => import('@redirector/front/pages/ResetPassword'));
const PostLoginRedirect = lazy(() => import('@redirector/front/pages/PostLoginRedirect'));

/** Standardized Loading Boundary */
const PageLoader = () => (
  <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
  </div>
);

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <PageLoader />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    /**
     * Suspense acts as a global error/loading boundary for the
     * underlying asynchronous chunk resolution.
     */
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<PostLoginRedirect />} />
          <Route path="redirections" element={<RedirectionsPage />} />
          <Route path="stats" element={<StatsPage />} />
          <Route path="stats/:id" element={<StatsDetailPage />} />
          <Route path="groups" element={<GroupsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
